import React, { useState } from 'react';
import axios from 'axios';
import PublicPageWrapper from '../../components/PublicPageWrapper';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Search, CheckCircle, Clock, Eye, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  submitted: { icon: Clock, color: 'bg-blue-50 text-blue-600', label: 'Submitted', desc: 'Your application has been received and is awaiting review.' },
  under_review: { icon: Eye, color: 'bg-amber-50 text-amber-600', label: 'Under Review', desc: 'Your application is currently being reviewed by our admissions team.' },
  accepted: { icon: CheckCircle, color: 'bg-emerald-50 text-emerald-600', label: 'Accepted', desc: 'Congratulations! Your application has been accepted.' },
  declined: { icon: XCircle, color: 'bg-red-50 text-red-600', label: 'Declined', desc: 'We regret to inform you that your application was not successful.' },
};

export default function StatusCheckerPage() {
  const [refNumber, setRefNumber] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const checkStatus = async (e) => {
    e?.preventDefault();
    if (!refNumber.trim()) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const { data } = await axios.get(`${API}/api/admissions/status/${refNumber.trim()}`);
      setResult(data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Application not found. Check your reference number.' : 'Error checking status');
    } finally { setLoading(false); }
  };

  const config = result ? statusConfig[result.status] : null;
  const StatusIcon = config?.icon || Clock;

  return (
    <PublicPageWrapper title="Application Status" subtitle="Enter your reference number to check your application.">
      <div className="max-w-xl mx-auto" data-testid="status-checker-page">
        <Card className="mb-6 border-slate-100">
          <CardContent className="p-5">
            <form onSubmit={checkStatus} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={refNumber} onChange={e => setRefNumber(e.target.value)} placeholder="e.g., TAD-2026-1234" className="pl-9 h-10 font-mono" data-testid="status-checker-input" />
              </div>
              <Button type="submit" disabled={loading} className="rounded-full bg-[#1B2A4A] h-10 px-5 text-xs font-bold" data-testid="status-checker-btn">{loading ? 'Checking...' : 'Check'}</Button>
            </form>
          </CardContent>
        </Card>
        {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center text-sm text-red-600 mb-5" data-testid="status-error">{error}</div>}
        {result && (
          <Card className="animate-fade-in border-slate-100" data-testid="status-result">
            <CardContent className="p-6 text-center">
              <div className={`w-14 h-14 rounded-full ${config?.color} flex items-center justify-center mx-auto mb-4`}><StatusIcon className="w-7 h-7" /></div>
              <h2 className="text-lg font-bold text-[#1B2A4A] mb-2">{result.reference_number}</h2>
              <Badge className={`${config?.color} text-xs px-3 py-1 mb-4`}>{config?.label}</Badge>
              <p className="text-sm text-slate-500 mb-5">{config?.desc}</p>
              <div className="text-left bg-slate-50 rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-400">Student</span><span className="font-semibold">{result.child_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Division</span><span>{result.division}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Grade</span><span>{result.grade}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Submitted</span><span>{new Date(result.submitted_at).toLocaleDateString()}</span></div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PublicPageWrapper>
  );
}
